import React, { useState, useEffect } from 'react';

const CONFERENCE_API = process.env.REACT_APP_CONFERENCE_API_URL || 'http://localhost:8080';
const TRACK_API = `${CONFERENCE_API}/api/tracks`;

const TrackManagement = () => {
  const [activeConferences, setActiveConferences] = useState([]);
  const [selectedConferenceId, setSelectedConferenceId] = useState('');
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [alert, setAlert] = useState(null);
  const [editingTrack, setEditingTrack] = useState(null);

  const [formData, setFormData] = useState({
    conferenceId: '',
    name: '',
    description: ''
  });

  const [errors, setErrors] = useState({});

  // Fetch active conferences
  const fetchActiveConferences = async () => {
    try {
      const response = await fetch(`${CONFERENCE_API}/active`);
      if (response.ok) {
        const data = await response.json();
        setActiveConferences(data);
      } else {
        showAlert('Failed to fetch conferences', 'error');
      }
    } catch (error) {
      console.error('Error fetching conferences:', error);
      showAlert('Error fetching conferences', 'error');
    }
  };

  // Fetch tracks by conference
  const fetchTracks = async (conferenceId) => {
    if (!conferenceId) {
      setTracks([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${TRACK_API}/conference/${conferenceId}`);
      if (response.ok) {
        const data = await response.json();
        setTracks(data);
      } else {
        showAlert('Failed to fetch tracks', 'error');
        setTracks([]);
      }
    } catch (error) {
      console.error('Error fetching tracks:', error);
      showAlert('Error fetching tracks', 'error');
      setTracks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveConferences();
  }, []);

  useEffect(() => {
    if (selectedConferenceId) {
      fetchTracks(selectedConferenceId);
    } else {
      setTracks([]);
    }
  }, [selectedConferenceId]);

  const showAlert = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.conferenceId) {
      newErrors.conferenceId = 'Conference is required';
    }
    if (!formData.name?.trim()) {
      newErrors.name = 'Track name is required';
    }
    if (!formData.description?.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleOpenModal = (mode, track = null) => {
    setModalMode(mode);
    if (mode === 'edit' && track) {
      setEditingTrack(track);
      setFormData({
        conferenceId: track.conferenceId,
        name: track.name,
        description: track.description || ''
      });
    } else {
      setEditingTrack(null);
      setFormData({
        conferenceId: selectedConferenceId || '',
        name: '',
        description: ''
      });
    }
    setErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTrack(null);
    setFormData({
      conferenceId: '',
      name: '',
      description: ''
    });
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const url = modalMode === 'create' 
        ? TRACK_API 
        : `${TRACK_API}/${editingTrack.id}`;
      
      const method = modalMode === 'create' ? 'POST' : 'PUT';

      // Trim all string values
      const trimmedData = {
        ...formData,
        trackName: formData.trackName?.trim()
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(trimmedData)
      });

      if (response.ok) {
        showAlert(`Track ${modalMode === 'create' ? 'created' : 'updated'} successfully!`, 'success');
        handleCloseModal();
        fetchTracks(selectedConferenceId || formData.conferenceId);
      } else {
        const errorText = await response.text();
        showAlert(errorText || `Failed to ${modalMode} track`, 'error');
      }
    } catch (error) {
      console.error(`Error ${modalMode}ing track:`, error);
      showAlert(`Error ${modalMode}ing track`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (trackId) => {
    if (!window.confirm('Are you sure you want to delete this track?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${TRACK_API}/${trackId}`, {
        method: 'DELETE'
      });

      if (response.ok || response.status === 204) {
        showAlert('Track deleted successfully!', 'success');
        fetchTracks(selectedConferenceId);
      } else {
        const errorText = await response.text();
        showAlert(errorText || 'Failed to delete track', 'error');
      }
    } catch (error) {
      console.error('Error deleting track:', error);
      showAlert('Error deleting track', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Track Management</h2>
        <button
          onClick={() => handleOpenModal('create')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#3498db',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
          disabled={!selectedConferenceId}
        >
          + Add Track
        </button>
      </div>

      {alert && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: alert.type === 'success' ? '#d4edda' : '#f8d7da',
          color: alert.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${alert.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {alert.message}
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
          Select Conference:
        </label>
        <select
          value={selectedConferenceId}
          onChange={(e) => setSelectedConferenceId(e.target.value)}
          style={{
            width: '100%',
            padding: '10px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        >
          <option value="">-- Select Conference --</option>
          {activeConferences.map(conf => (
            <option key={conf.id} value={conf.id}>
              {conf.name}
            </option>
          ))}
        </select>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>}

      {!loading && selectedConferenceId && tracks.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          No tracks found for this conference. Click "Add Track" to create one.
        </div>
      )}

      {!loading && tracks.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>ID</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Name</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Description</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Conference</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tracks.map(track => (
                <tr key={track.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '12px' }}>{track.id}</td>
                  <td style={{ padding: '12px' }}>{track.name}</td>
                  <td style={{ padding: '12px' }}>{track.description || '-'}</td>
                  <td style={{ padding: '12px' }}>{track.conferenceName || '-'}</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleOpenModal('edit', track)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#ffc107',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginRight: '8px',
                        fontSize: '12px'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(track.id)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>
              {modalMode === 'create' ? 'Create Track' : 'Edit Track'}
            </h3>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Conference: <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  name="conferenceId"
                  value={formData.conferenceId}
                  onChange={handleInputChange}
                  disabled={modalMode === 'edit'}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: errors.conferenceId ? '1px solid red' : '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">-- Select Conference --</option>
                  {activeConferences.map(conf => (
                    <option key={conf.id} value={conf.id}>
                      {conf.name}
                    </option>
                  ))}
                </select>
                {errors.conferenceId && (
                  <span style={{ color: 'red', fontSize: '12px' }}>{errors.conferenceId}</span>
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Track Name: <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Machine Learning"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: errors.name ? '1px solid red' : '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                {errors.name && (
                  <span style={{ color: 'red', fontSize: '12px' }}>{errors.name}</span>
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Description: <span style={{ color: 'red' }}>*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Track description"
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: errors.description ? '1px solid red' : '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
                {errors.description && (
                  <span style={{ color: 'red', fontSize: '12px' }}>{errors.description}</span>
                )}
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: loading ? '#ccc' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {loading ? 'Saving...' : modalMode === 'create' ? 'Create' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackManagement;
